import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { PlainButton } from 'components/v3';
import { formatAnswer, formatTin } from '../../shared/answerFormat';
import Glyph from '../Glyph';
import { ORANGE, sx } from '../sx';
import { gridRows } from './entryExport';
import { hasFiles, isAgreement } from './entryModel';

// One answer on the entry card, read-only (design entry_detail.jsx
// ValueView): agreements as Agreed / Not agreed, checkbox picks as tags,
// stars for a rating, a small table for repeaters and tables, the file's
// name as a download button, and "—" for a blank.
//
// W-9 system types (w9-plan §6.3 item 9, T16): a `tin` shows the stored
// mask ("SSN · ***-**-1234") with a Reveal button when `onReveal` is given
// and the full number while `revealed` holds it; a `signature` shows the
// typed name in a script face, or the drawn PNG when `imageUrl` is given
// (else a button that fetches it through onDownload). All optional: a
// regular form never passes them.

const Empty = styled.span`
  color: ${(p) => sx(p.theme).faint};
  font-weight: 500;
`;

const Agreed = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${(p) => sx(p.theme).ok};
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 2px;
`;

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  box-sizing: border-box;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid ${(p) => sx(p.theme).line2};
  background: ${(p) => sx(p.theme).inset};
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  color: ${(p) => sx(p.theme).text};
`;

const Long = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.55;
  color: ${(p) => sx(p.theme).text2};
  white-space: pre-wrap;
`;

const Stars = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: ${(p) => sx(p.theme).disabled};
`;

// A filled star: CSS fill beats the svg's fill="none" attribute.
export const Star = styled.span`
  display: flex;
  color: ${(p) => (p.$on ? '#F5B54D' : 'inherit')};

  svg {
    fill: ${(p) => (p.$on ? '#F5B54D' : 'none')};
  }
`;

const StarCount = styled.span`
  margin-left: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => sx(p.theme).label};
`;

export const MiniGrid = styled.div`
  margin-top: 4px;
  border: 1px solid ${(p) => sx(p.theme).line2};
  border-radius: 10px;
  overflow: hidden;
  font-size: 13.5px;
`;

export const MiniRow = styled.div`
  display: grid;
  gap: 10px;
  align-items: center;
  padding: ${(p) => (p.$head ? '7px 12px' : '8px 12px')};
  border-top: ${(p) => (p.$first ? '0' : `1px solid ${sx(p.theme).line}`)};
  background: ${(p) => (p.$head ? sx(p.theme).inset : 'transparent')};
  font-size: ${(p) => (p.$head ? '10.5px' : 'inherit')};
  font-weight: ${(p) => (p.$head ? 700 : 600)};
  letter-spacing: ${(p) => (p.$head ? '1.2px' : 'normal')};
  text-transform: ${(p) => (p.$head ? 'uppercase' : 'none')};
  color: ${(p) => (p.$head ? sx(p.theme).label : sx(p.theme).text)};

  & > div {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const FileButton = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  font-weight: 600;
  color: ${(p) => sx(p.theme).otext};
  text-decoration: underline;
  text-underline-offset: 3px;
  word-break: break-all;
  text-align: left;

  &:disabled {
    opacity: 0.6;
  }
`;

const Lines = styled.div`
  display: grid;
  gap: 4px;
`;

const TinRow = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  font-variant-numeric: tabular-nums;
`;

const RevealButton = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid ${(p) => sx(p.theme).tintB};
  background: ${(p) => sx(p.theme).tint};
  color: ${(p) => sx(p.theme).otext};
  font-size: 12.5px;
  font-weight: 700;

  &:hover:not(:disabled) {
    border-color: ${ORANGE};
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const Revealed = styled.span`
  color: ${(p) => sx(p.theme).otext};
  font-weight: 700;
`;

const SignatureBlock = styled.span`
  display: grid;
  gap: 6px;
`;

// The typed signature's face: a script font, falling back to cursive.
export const SIGNATURE_FONT =
  "'Brush Script MT', 'Snell Roundhand', 'Segoe Script', 'Apple Chancery', cursive";

const ScriptName = styled.span`
  font-family: ${SIGNATURE_FONT};
  font-size: 26px;
  line-height: 1.15;
  font-weight: 400;
  color: ${(p) => sx(p.theme).text};
`;

const SignatureImage = styled.img`
  display: block;
  max-width: 320px;
  max-height: 120px;
  width: auto;
  height: auto;
  border: 1px solid ${(p) => sx(p.theme).line2};
  border-radius: 8px;
  background: #ffffff;
`;

const SignatureMode = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${(p) => sx(p.theme).label};
`;

export const REVEAL_LABEL = 'Reveal';

const TinValue = ({ field, value, onReveal, revealed, revealing }) => {
  const masked = formatTin(value);
  if (!masked) return <Empty>—</Empty>;
  const kind = String(value.kind || '').toUpperCase();
  if (revealed) {
    return (
      <TinRow aria-live="polite">
        <span>{`${kind} · `}</span>
        <Revealed data-fb-tin-revealed="">{revealed}</Revealed>
      </TinRow>
    );
  }
  return (
    <TinRow>
      <span>{masked}</span>
      {onReveal ? (
        <RevealButton
          onClick={(e) => {
            e.stopPropagation();
            onReveal(field.id);
          }}
          disabled={revealing}
          aria-label={`${REVEAL_LABEL} ${field.label || 'TIN'}`}
        >
          <Glyph name="eye" size={13} />
          {revealing ? 'Revealing…' : REVEAL_LABEL}
        </RevealButton>
      ) : null}
    </TinRow>
  );
};

TinValue.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.any,
  onReveal: PropTypes.func,
  revealed: PropTypes.string,
  revealing: PropTypes.bool,
};
TinValue.defaultProps = {
  value: undefined,
  onReveal: undefined,
  revealed: null,
  revealing: false,
};

const SignatureValue = ({ value, imageUrl, onDownload, downloading }) => {
  const name =
    value && typeof value === 'object' ? String(value.name || '').trim() : '';
  if (!name) return <Empty>—</Empty>;
  const drawn = value.mode === 'drawn';
  if (drawn && imageUrl) {
    return (
      <SignatureBlock>
        <SignatureImage src={imageUrl} alt={`Signature of ${name}`} />
        <SignatureMode>{`Signed by ${name} (drawn)`}</SignatureMode>
      </SignatureBlock>
    );
  }
  if (drawn) {
    return (
      <SignatureBlock>
        <span>{`Signed by ${name}`}</span>
        {value.file_id != null ? (
          <FileButton
            onClick={(e) => {
              e.stopPropagation();
              onDownload(value.file_id);
            }}
            disabled={downloading}
            aria-label={`View signature of ${name}`}
          >
            <Glyph name="paperclip" size={15} />
            {downloading ? 'Getting the signature…' : 'View drawn signature'}
          </FileButton>
        ) : (
          <SignatureMode>drawn</SignatureMode>
        )}
      </SignatureBlock>
    );
  }
  return (
    <SignatureBlock>
      <ScriptName aria-label={`Signature of ${name}`} data-fb-signature="">
        {name}
      </ScriptName>
      <SignatureMode>{`Signed by ${name} (typed)`}</SignatureMode>
    </SignatureBlock>
  );
};

SignatureValue.propTypes = {
  value: PropTypes.any,
  imageUrl: PropTypes.string,
  onDownload: PropTypes.func.isRequired,
  downloading: PropTypes.bool.isRequired,
};
SignatureValue.defaultProps = { value: undefined, imageUrl: null };

export const gridTemplate = (count) => ({
  gridTemplateColumns: `repeat(${Math.max(1, count)}, minmax(0, 1fr))`,
});

const GridValue = ({ field, value }) => {
  const columns = (field.config && field.config.columns) || [];
  const rows = gridRows(field, value);
  if (!rows.length) return <Empty>—</Empty>;
  const tpl = gridTemplate(columns.length);
  return (
    <MiniGrid>
      <MiniRow $head $first style={tpl}>
        {columns.map((c, i) => (
          <div key={i}>{c}</div>
        ))}
      </MiniRow>
      {rows.map((row, r) => (
        <MiniRow key={r} style={tpl}>
          {row.map((cell, c) => (
            <div key={c}>{cell || '—'}</div>
          ))}
        </MiniRow>
      ))}
    </MiniGrid>
  );
};

GridValue.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.any,
};
GridValue.defaultProps = { value: undefined };

const RatingValue = ({ field, value }) => {
  const n = Number(value) || 0;
  const max = (field.config && field.config.max) || 5;
  if (!n) return <Empty>—</Empty>;
  return (
    <Stars role="img" aria-label={`${n} of ${max} stars`}>
      {Array.from({ length: max }, (_, i) => (
        <Star key={i} $on={i < n}>
          <Glyph name="star" size={15} />
        </Star>
      ))}
      <StarCount>{`${n}/${max}`}</StarCount>
    </Stars>
  );
};

RatingValue.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.any,
};
RatingValue.defaultProps = { value: undefined };

const EntryValue = ({
  field,
  value,
  onDownload,
  downloading,
  onReveal,
  revealed,
  revealing,
  imageUrl,
}) => {
  if (field.type === 'tin') {
    return (
      <TinValue
        field={field}
        value={value}
        onReveal={onReveal}
        revealed={revealed}
        revealing={revealing}
      />
    );
  }
  if (field.type === 'signature') {
    return (
      <SignatureValue
        value={value}
        imageUrl={imageUrl}
        onDownload={onDownload}
        downloading={downloading}
      />
    );
  }
  if (isAgreement(field)) {
    return value === true ? (
      <Agreed>
        <Glyph name="circle-check" size={15} />
        Agreed
      </Agreed>
    ) : (
      <Empty>Not agreed</Empty>
    );
  }
  if (field.type === 'checkbox') {
    const picked = Array.isArray(value) ? value : [];
    return picked.length ? (
      <Tags>
        {picked.map((option) => (
          <Tag key={option}>{option}</Tag>
        ))}
      </Tags>
    ) : (
      <Empty>—</Empty>
    );
  }
  if (field.type === 'file_upload') {
    if (!hasFiles(field, value)) return <Empty>No file</Empty>;
    return (
      <FileButton
        onClick={(e) => {
          e.stopPropagation();
          onDownload(value.file_id);
        }}
        disabled={downloading}
        aria-label={`Download ${value.filename || 'file'}`}
      >
        <Glyph name="paperclip" size={15} />
        {downloading ? 'Getting the file…' : value.filename || 'Download file'}
      </FileButton>
    );
  }
  if (field.type === 'rating')
    return <RatingValue field={field} value={value} />;
  if (field.type === 'repeater' || field.type === 'table') {
    return <GridValue field={field} value={value} />;
  }
  if (field.type === 'matrix') {
    const rows = ((field.config && field.config.rows) || []).filter(
      (row) => value && value[row]
    );
    if (!rows.length) return <Empty>—</Empty>;
    return (
      <Lines>
        {rows.map((row) => (
          <span key={row}>{`${row}: ${value[row]}`}</span>
        ))}
      </Lines>
    );
  }
  const text = formatAnswer(field, value);
  if (!text) return <Empty>—</Empty>;
  return field.type === 'long_answer' ? <Long>{text}</Long> : text;
};

EntryValue.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    config: PropTypes.object,
  }).isRequired,
  value: PropTypes.any,
  onDownload: PropTypes.func,
  downloading: PropTypes.bool,
  // W-9 only (w9-plan T11, T16); a regular form never passes these.
  onReveal: PropTypes.func,
  revealed: PropTypes.string,
  revealing: PropTypes.bool,
  imageUrl: PropTypes.string,
};

EntryValue.defaultProps = {
  value: undefined,
  onDownload: () => undefined,
  downloading: false,
  onReveal: undefined,
  revealed: null,
  revealing: false,
  imageUrl: null,
};

export default EntryValue;
